import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { useResetMutation } from '@/features/auth/useAuthMutations';
import { resetSchema, type ResetInput } from '@/lib/zodSchemas/auth';

export default function ResetPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const mutation = useResetMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetInput>({
    resolver: zodResolver(resetSchema),
    defaultValues: { token, password: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    await mutation.mutateAsync({ token: values.token, password: values.password });
    navigate('/login', { replace: true });
  });

  if (!token) {
    return (
      <div>
        <h1 className="font-display text-3xl font-semibold">Token inválido</h1>
        <p className="mt-2 text-sm text-(--color-fg-muted)">
          O link de redefinição é inválido ou expirou.
        </p>
        <Link to="/forgot" className="mt-6 inline-block text-(--color-accent) hover:underline">
          Solicitar novo link
        </Link>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-display text-3xl font-semibold">Nova senha</h1>
      <p className="mt-2 text-sm text-(--color-fg-muted)">Defina uma nova senha para sua conta.</p>

      <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-4" noValidate>
        <input type="hidden" {...register('token')} />
        <FormField id="password" label="Nova senha" error={errors.password?.message} required>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            invalid={!!errors.password}
            {...register('password')}
          />
        </FormField>
        <FormField
          id="confirmPassword"
          label="Confirmar senha"
          error={errors.confirmPassword?.message}
          required
        >
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            invalid={!!errors.confirmPassword}
            {...register('confirmPassword')}
          />
        </FormField>
        {mutation.isError ? (
          <p className="text-sm text-(--color-error)" role="alert">
            Não foi possível redefinir a senha. O link pode ter expirado.
          </p>
        ) : null}
        <Button type="submit" disabled={isSubmitting || mutation.isPending}>
          {mutation.isPending ? 'Salvando…' : 'Salvar nova senha'}
        </Button>
      </form>
    </div>
  );
}
